<template>
  <v-dialog
    :value="value"
    max-width="820"
    content-class="other-svgs-dialog rounded-xl"
    overlay-opacity="0.75"
    transition="dialog-transition"
    @input="$emit('input', $event)"
  >
    <v-card dark class="other-svgs-card rounded-xl">
      <div class="other-svgs-header">
        <div class="other-svgs-header-inner">
          <h2 class="other-svgs-title">Other SVGs</h2>
          <v-btn icon class="other-svgs-close-btn" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-card-text class="other-svgs-modal-body">
        <template v-if="step === 'list'">
          <p class="other-svgs-intro text-body-2 text--secondary mb-3">
            Products whose name includes <strong>Initials</strong> or
            <strong>birth data</strong> (<em>Geburtsdaten</em>). Select one to edit fields and
            generate an SVG.
          </p>
          <v-list dense class="other-svgs-list pa-0">
            <v-list-item
              v-for="(entry, idx) in entries"
              :key="entryKey(entry, idx)"
              class="other-svgs-list-item"
              @click="openDetail(entry)"
            >
              <v-list-item-content>
                <v-list-item-title class="other-svgs-list-title d-flex align-center flex-wrap">
                  <span class="other-svgs-list-title-text">{{ entry.title }}</span>
                  <v-chip x-small label class="other-svgs-cat-chip" color="primary" outlined>
                    {{ entry.categoryLabel }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
              <v-icon class="other-svgs-chevron" small color="primary">mdi-chevron-right</v-icon>
            </v-list-item>
            <p v-if="!entries.length" class="other-svgs-empty">
              No matching line items in this order.
            </p>
          </v-list>
        </template>

        <v-row v-else no-gutters class="other-svgs-row">
          <v-col cols="12" md="6" class="other-svgs-left-col">
            <div class="other-svgs-form-panel">
              <v-btn text small class="other-svgs-back px-0 mb-2" @click="goList">
                <v-icon left small>mdi-arrow-left</v-icon>
                All products
              </v-btn>
              <div class="other-svgs-detail-head mb-3">
                <h3 class="other-svgs-detail-title">{{ activeEntry.title }}</h3>
                <v-chip x-small label class="other-svgs-cat-chip" color="primary" outlined>
                  {{ activeEntry.categoryLabel }}
                </v-chip>
              </div>
              <TextLinesInput
                label="SVG inputs"
                :value="fieldValues"
                :fields="activeFields"
                @input="fieldValues = $event"
              />
              <v-btn
                color="primary"
                :loading="loading"
                block
                class="other-svgs-generate-btn mt-3"
                :disabled="!canGenerate"
                @click="generateSvg"
              >
                <v-icon left small>mdi-auto-fix</v-icon>
                Generate SVG
              </v-btn>
              <v-alert
                v-if="generationNotice"
                dense
                text
                type="info"
                class="mt-3 mb-0 other-svgs-alert"
              >
                {{ generationNotice }}
              </v-alert>
            </div>
          </v-col>
          <v-col cols="0" md="1" />
          <v-col cols="12" md="4" class="other-svgs-preview-col">
            <SvgPreviewWithDownload
              label="Preview"
              :src="svgUrl"
              :download-filename="svgDownloadFilename || 'other.svg'"
              placeholder-text="SVG preview will appear here"
              placeholder-hint="Enter text and click Generate SVG"
              @download="$emit('download')"
            />
          </v-col>
          <v-col cols="0" md="1" />
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import TextLinesInput from '../BacksideEngraving/TextLinesInput.vue'
  import SvgPreviewWithDownload from '../BacksideEngraving/SvgPreviewWithDownload.vue'
  import { generateSvg } from '@/services/pdfGenerationService'
  import {
    getOtherSvgLineEntries,
    getOtherSvgPrefillFields,
    getOtherSvgFieldsForCategory,
    getOtherSvgLineKeysForCategory,
    buildOtherSvgLines,
  } from './orderHelpers'
  import { OTHER_SVG_CATEGORY, OTHER_SVG_TEMPLATE_BY_CATEGORY } from './constants'

  export default {
    name: 'OtherSvgsModal',
    components: { TextLinesInput, SvgPreviewWithDownload },
    props: {
      value: { type: Boolean, default: false },
      order: { type: Object, default: () => ({}) },
    },
    data() {
      return {
        step: 'list',
        activeEntry: null,
        fieldValues: {},
        svgUrl: null,
        svgDownloadFilename: null,
        loading: false,
        generationNotice: '',
      }
    },
    computed: {
      entries() {
        return getOtherSvgLineEntries(this.order)
      },
      activeFields() {
        return getOtherSvgFieldsForCategory(this.activeEntry?.category)
      },
      canGenerate() {
        return Object.values(this.fieldValues).some((t) => t != null && String(t).trim() !== '')
      },
    },
    watch: {
      value(open) {
        if (open) {
          this.resetModal()
        }
      },
    },
    methods: {
      entryKey(entry, idx) {
        const id = entry?.lineItem?.id
        return id != null ? `os-${id}` : `os-${idx}`
      },
      resetModal() {
        this.step = 'list'
        this.activeEntry = null
        this.fieldValues = {}
        this.svgUrl = null
        this.svgDownloadFilename = null
        this.generationNotice = ''
        this.loading = false
      },
      openDetail(entry) {
        this.activeEntry = entry
        this.step = 'detail'
        this.svgUrl = null
        this.svgDownloadFilename = null
        this.generationNotice = ''
        const li = entry.lineItem
        this.fieldValues = getOtherSvgPrefillFields(li, entry.category)
        this.$nextTick(() => this.generateSvg())
      },
      goList() {
        this.step = 'list'
        this.activeEntry = null
        this.fieldValues = {}
        this.svgUrl = null
        this.svgDownloadFilename = null
        this.generationNotice = ''
      },
      async generateSvg() {
        if (!this.canGenerate || !this.activeEntry) return
        const lines = buildOtherSvgLines(
          this.fieldValues,
          getOtherSvgLineKeysForCategory(this.activeEntry.category)
        )
        if (!lines.length) return

        this.loading = true
        this.generationNotice = ''
        this.svgUrl = null
        this.svgDownloadFilename = null
        try {
          const orderNum = this.order?.orderNumber || 'order'
          const category = this.activeEntry.category
          const template = OTHER_SVG_TEMPLATE_BY_CATEGORY[category]
          if (!template) {
            this.generationNotice = 'Unknown product category for SVG template.'
            return
          }
          const filename = `${orderNum}_${
            category === OTHER_SVG_CATEGORY.BIRTH_DATA ? 'birth_data' : 'initials'
          }_${Date.now()}.svg`
          const payload = {
            filename,
            template,
            data: { lines, ...(template === 'svg/circle-text' && { options: { fontSize: 60 } }) },
          }
          const res = await generateSvg(payload)
          const data = res?.data?.data ?? res?.data
          const url = data?.url
          if (url) {
            this.svgUrl = url
            this.svgDownloadFilename = data?.filename || filename
            this.$emit('generated', { url, filename: this.svgDownloadFilename, payload })
          } else {
            this.generationNotice = 'No image URL in the server response.'
          }
        } catch (err) {
          console.error('Other SVG generation failed:', err)
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Generation failed. Try again or check the console.'
          this.generationNotice = Array.isArray(msg) ? msg.join(', ') : String(msg)
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style scoped>
  .other-svgs-dialog >>> .v-dialog {
    box-shadow: 0 32px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04);
  }

  .other-svgs-card {
    background: #1c1c1f !important;
    border: 1px solid rgba(255, 255, 255, 0.06);
    overflow: hidden;
  }

  .other-svgs-header {
    padding: 0;
    background: rgba(255, 255, 255, 0.02);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  .other-svgs-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
  }

  .other-svgs-title {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.95);
  }

  .other-svgs-close-btn {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 50% !important;
    color: #fff !important;
    background-color: #e53935 !important;
    transition: color 0.2s ease, background-color 0.2s ease;
  }

  .other-svgs-close-btn .v-icon {
    font-size: 18px !important;
  }

  .other-svgs-close-btn:hover {
    color: #fff !important;
    background-color: #f44336 !important;
  }

  .other-svgs-modal-body {
    min-height: 280px;
    padding: 14px 16px 16px !important;
  }

  .other-svgs-intro strong {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
  }

  .other-svgs-list {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    max-height: 320px;
    overflow-y: auto;
  }

  .other-svgs-list-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    min-height: 56px !important;
    cursor: pointer;
  }

  .other-svgs-list-item:last-of-type {
    border-bottom: none;
  }

  .other-svgs-list-item:hover {
    background: rgba(255, 255, 255, 0.04) !important;
  }

  .other-svgs-list-title {
    font-size: 0.875rem !important;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.92) !important;
    line-height: 1.35 !important;
    gap: 8px;
  }

  .other-svgs-list-title-text {
    flex: 1;
    min-width: 0;
  }

  .other-svgs-cat-chip {
    flex-shrink: 0;
    margin-top: 0;
    height: 22px !important;
    font-size: 0.65rem !important;
    text-transform: none;
    letter-spacing: 0.02em;
  }

  .other-svgs-chevron {
    opacity: 0.85;
  }

  .other-svgs-empty {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.35);
    font-style: italic;
    padding: 16px;
    margin: 0;
  }

  .other-svgs-row {
    align-items: stretch;
  }

  .other-svgs-left-col {
    padding-right: 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
  }

  .other-svgs-form-panel {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    padding: 12px 14px;
  }

  .other-svgs-back {
    text-transform: none;
    letter-spacing: 0.02em;
    font-weight: 600;
  }

  .other-svgs-detail-head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .other-svgs-detail-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.95);
  }

  .other-svgs-form-panel >>> .text-lines-input {
    margin-bottom: 0;
  }

  .other-svgs-generate-btn {
    text-transform: none;
    font-weight: 600;
    font-size: 0.875rem !important;
    letter-spacing: 0.02em;
    border-radius: 8px !important;
  }

  .other-svgs-alert {
    font-size: 0.8125rem !important;
  }

  @media (min-width: 960px) {
    .other-svgs-preview-col {
      padding-left: 20px;
    }
  }
</style>
